import {
  Avatar,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material'
import { SkeletonText } from 'utoniq-ui'

import {
  GachaItemsWithRemainingCountInfoFragment,
  GachaRollAlgorithm,
  Maybe,
} from '@/clients/utoniq-core/schema'
import { BaseLayout, Head } from '@/components/layouts'
import { ScrollFetcher } from '@/components/organisms/functionals'
import { config } from '@/config'
import { useRouter, useTranslation } from '@/core'
import { Cell, Template } from '@/core/architecture'

export * from './fragments'

export type State = {
  loading: boolean
  rollAlgorithm: Maybe<GachaRollAlgorithm>
  items: Maybe<GachaItemsWithRemainingCountInfoFragment[]>
}

type ItemRateCellProps = {
  node: GachaItemsWithRemainingCountInfoFragment
  rollAlgorithm: Maybe<GachaRollAlgorithm>
}

/**
 * Action type.
 * use key name with 'on' prefix like onLogin.
 */
export type Action = {}

export const GachaItemListTemplate: Template<State, Action> = ({ state }) => {
  const { t } = useTranslation('gachaItemList')
  const { routerNames, routerPages, generatePath, router } =
    useRouter<{ gachaId: string; artistId?: string }>()
  return (
    <BaseLayout
      header={{
        back: true,
        defaultBackPage: generatePath(routerPages.ArtistGachaDetail, {
          gachaId: router.query.gachaId ?? '#',
          artistId: router.query.artistId ?? config.artistId ?? '#',
        }),
        title: t('title'),
      }}
    >
      <Head title={routerNames.ArtistGachaDetail} />
      <Typography sx={{ pt: 2, px: 5, pb: 3 }} variant="body2">
        {t('desc')}
      </Typography>
      <Stack
        direction="row"
        justifyContent="space-between"
        sx={{ px: 5, bgcolor: 'grey.50', py: '6px' }}
        alignItems="center"
      >
        <Typography variant="subtitle1">{t('itemName')}</Typography>
        <Typography variant="subtitle1">
          {t(
            state.rollAlgorithm === GachaRollAlgorithm.Limited
              ? 'remainingCount'
              : 'provided'
          )}
        </Typography>
      </Stack>
      <ScrollFetcher
        component={List}
        componentProps={{ sx: { pt: 4 } }}
        nodes={state.items?.map(node => (
          <ItemRateCell
            node={node}
            key={node.item.id}
            rollAlgorithm={state.rollAlgorithm}
          />
        ))}
        loading={state.loading}
        hasNextPage={false}
        onFetchMore={async () => {}}
        loadingComponent={
          <List sx={{ pt: 2 }}>
            {Array.from(Array(6).keys()).map(key => (
              <ListItem
                key={key}
                sx={{ height: 'auto', px: 5 }}
                secondaryAction={<SkeletonText variant="medium" width={40} />}
              >
                <ListItemAvatar>
                  <Skeleton variant="rectangular" width={48} height={76} />
                </ListItemAvatar>
                <SkeletonText variant="medium" width="50%" />
              </ListItem>
            ))}
          </List>
        }
      />
    </BaseLayout>
  )
}

const ItemRateCell: Cell<ItemRateCellProps> = ({
  rollAlgorithm,
  node: { item, weight, remainingCountInfo },
}) => (
  <ListItem sx={{ height: 'auto', px: 5, pt: 0, pb: 3 }}>
    <ListItemAvatar>
      <Avatar
        variant="square"
        src={item.thumbnail.front?.smallUri ?? ''}
        sx={{ width: 48, height: 76 }}
      />
    </ListItemAvatar>
    <ListItemText
      disableTypography
      slotProps={{
        primary: { variant: 'overline' },
      }}
    >
      <Stack direction="row" justifyContent="space-between">
        <Typography variant="overline">{item.title}</Typography>
        <Typography variant="overline">
          {rollAlgorithm === GachaRollAlgorithm.Limited
            ? remainingCountInfo
              ? `${remainingCountInfo.remainingCount}/${remainingCountInfo.allCount}`
              : '-/-'
            : weight}
        </Typography>
      </Stack>
    </ListItemText>
  </ListItem>
)
